import { z } from 'zod';

export const getPageFieldset = (args: {
  isFieldZipAdded: boolean;
  isFieldEmailAdded: boolean;
  isFieldCityAdded: boolean;
  isFieldStreetAdded: boolean;
  isFieldHouseNumberAdded: boolean;
  isFirstNameAdded: boolean;
  isLastNameAdded: boolean;
}): PageFieldsetFieldType[] => {
  const {
    isFieldZipAdded,
    isFieldEmailAdded,
    isFieldCityAdded,
    isFieldStreetAdded,
    isFieldHouseNumberAdded,
    isFirstNameAdded,
    isLastNameAdded,
  } = args;

  let fieldset: PageFieldsetFieldType[] = [
    {
      type: PageFieldsetFieldTypeEnum.MOBILE_PHONE,
      required: true,
    },
  ];

  if (isFieldZipAdded) {
    fieldset.push({
      type: PageFieldsetFieldTypeEnum.ZIP,
      required: false,
    });
  }

  if (isFieldEmailAdded) {
    fieldset.push({
      type: PageFieldsetFieldTypeEnum.EMAIL,
      required: false,
    });
  }

  if (isFieldCityAdded) {
    fieldset.push({
      type: PageFieldsetFieldTypeEnum.CITY,
      required: false,
    });
  }

  if (isFieldStreetAdded) {
    fieldset.push({
      type: PageFieldsetFieldTypeEnum.STREET,
      required: false,
    });
  }

  if (isFieldHouseNumberAdded) {
    fieldset.push({
      type: PageFieldsetFieldTypeEnum.HOUSE_NUMBER,
      required: false,
    });
  }

  if (isFirstNameAdded) {
    fieldset.push({
      type: PageFieldsetFieldTypeEnum.FIRST_NAME,
      required: false,
    });
  }

  if (isLastNameAdded) {
    fieldset.push({
      type: PageFieldsetFieldTypeEnum.LAST_NAME,
      required: false,
    });
  }

  return fieldset;
};

// Base page fields (common to all pages)
export const PageBaseSchema = z.object({
  title: z.string(),
  description: z.string(),
  seoTitle: z.string(),
  seoDescription: z.string(),
  seoKeywords: z.string(),
  backgroundUrl: z.string().optional(),
  backgroundShadow: z.boolean().optional(),
  backgroundColor: z.string().optional(),
  path: z.string().startsWith('/'),
});

// Optional base update schema for pages
export const PageBaseUpdateSchema = z
  .object({
    title: z.string(),
    description: z.string(),
    seoTitle: z.string(),
    seoDescription: z.string(),
    seoKeywords: z.string(),
    backgroundUrl: z.string().optional().or(z.null()),
    backgroundColor: z.string().optional(),
    backgroundShadow: z.boolean().optional(),
  })
  .partial();

// --- Common Page Data for all pages ---
export enum PageFieldsetFieldTypeEnum {
  MOBILE_PHONE = 'MOBILE_PHONE',
  ZIP = 'ZIP',
  EMAIL = 'EMAIL',
  CITY = 'CITY',
  STREET = 'STREET',
  HOUSE_NUMBER = 'HOUSE_NUMBER',
  FIRST_NAME = 'FIRST_LAST_NAME',
  LAST_NAME = 'LAST_NAME',
}

export const PageFieldsetFieldSchema = z.object({
  type: z.nativeEnum(PageFieldsetFieldTypeEnum),
  required: z.boolean().default(true),
});

export type PageFieldsetFieldType = z.infer<typeof PageFieldsetFieldSchema>;

export const PageDataButtonTextZodType = z.string().min(2).max(50);

// Unified page data (all pages share the same data structure)
export const PageDataSchema = z.object({
  buttonText: PageDataButtonTextZodType,
  fieldset: z.array(PageFieldsetFieldSchema),
});

export type PageDataType = z.infer<typeof PageDataSchema>;

/**
 * Enum for the page types. These should match the values defined in your database enum.
 */
export enum PageType {
  WHATSAPP_COMMUNITY_REGISTRATION = 'whatsapp_community_registration',
  WHATSAPP_CHANNEL_REGISTRATION = 'whatsapp_channel_registration',
  LOTTERY = 'lottery',
}

/**
 * Zod type for the page type enum.
 */
export const PageTypeZodType = z.nativeEnum(PageType);

/**
 * CreatePageSchema
 *
 * This schema is used when creating a new page. It combines the base page fields,
 * the page-specific fields (like `pageCollectionId` and `type`), and the unified page data.
 *
 * The resulting shape will include:
 * - All required fields from PageBaseSchema (e.g. title, description, seoTitle, etc.)
 * - `pageCollectionId` (string)
 * - `type` (one of the values from PageType)
 * - `data` (the page data following PageDataSchema)
 */
export const CreatePageSchema = PageBaseSchema.extend({
  pageCollectionId: z.string(),
  type: PageTypeZodType,
  data: PageDataSchema,
});

export type CreatePageType = z.infer<typeof CreatePageSchema>;

/**
 * UpdatePageSchema
 *
 * When updating a page, you typically do not allow changing fields like `pageCollectionId`
 * or `type`. Here, we use the optional (partial) base update schema for the common fields,
 * and require a new `data` payload. Adjust the requirements as needed.
 */
export const UpdatePageSchema = PageBaseUpdateSchema.extend({
  data: PageDataSchema,
});

export type UpdatePageType = z.infer<typeof UpdatePageSchema>;
